import React, { useState, useEffect, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import DimensionsForm from "./DimensionsForm";
import PatternForm from "./PatternForm";
import TypeForm from "./TypeForm";
import TextureForm from "./TextureForm";
import InstallationTypeNotice from "./InstallationTypeNotice";
import { FormButtonNext } from "../Stepper/FormButtonNext";
import { FormButtonPrevious } from "../Stepper/FormButtonPrevious";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddToCartButton from "../Stepper/AddToCartButton";
import { fetchPublicProducts, selectProductLocally } from "../../helpers/ProductsApi";
import { fetchMessageRules, evaluateMessageRules } from "../../helpers/MessageRulesApi";
import {
  evalCacheDimFormula,
  CACHE_DIM_HARD_MINIMUMS,
  applyMinFrontToRearHeight,
} from "../../helpers/evalCacheDimFormula";
import { resolveDecValues } from "../../helpers/decHelpers";
import {
  urlHasCustomCacheDimensions,
  urlHasInstallDimensions,
} from "../../helpers/shareUrlParams";

const ConfiguratorForm = forwardRef(({
  step,
  modelFormData,
  materialFormData,
  patterns,
  textures,
  onModelFormChange,
  onModelFormBatchChange,
  onMetadataInfosChange,
  onMaterialFormChange,
  onQuotationChange,
  handleStepChange,
  quotationDatas,
  price,
  isButtonCartDisabled,
  handleAddToCart,
  hidePrice,
  isPriceStale = false,
  dimFormulas,
  cacheMinimumsConfig = null,
  productDec = null,
  selectedProductId = null,
  isProductConfigReady = false,
  onCacheDimensionsReady,
  onHasMatchingProductChange,
  onInstallDimDraftChange,
}, ref) => {
  const dimensionsFormRef = useRef(null);
  const [dimensionsErrorString, setDimensionsErrorString] = useState(null);
  const [dimensionsNeedDevis, setDimensionsNeedDevis] = useState(false);
  const [isDimensionsError, setIsDimensionsError] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const [previousL, setPreviousL] = useState(0);
  const [previousH, setPreviousH] = useState(0);
  const [previousP1, setPreviousP1] = useState(0);
  const [previousP2, setPreviousP2] = useState(0);
  const [previousType, setPreviousType] = useState(null);
  const [previousBottom, setPreviousBottom] = useState(null);
  const cacheRecalcKeyRef = useRef(null);
  /** Dernier choix fixation (murale/au sol) pour `sol-mur` — restauré au retour sur ce type. */
  const solMurWallmountRef = useRef(null);
  const urlInstallRecalcKeyRef = useRef("");

  // 🆕 Charger les produits disponibles pour la sélection dynamique
  const [availableProducts, setAvailableProducts] = useState([]);
  
  // 🆕 Charger les règles de messages dynamiques
  const [messageRules, setMessageRules] = useState([]);
  const [messageSettings, setMessageSettings] = useState({ maxDimensionLimit: 2000 });
  
  useEffect(() => {
    // Charger les produits
    fetchPublicProducts().then(products => {
      setAvailableProducts(products || []);
    }).catch(err => {
      console.warn('[ConfiguratorForm] Impossible de charger les produits:', err);
    });
    
    // Charger les règles de messages
    fetchMessageRules().then(({ rules, settings }) => {
      setMessageRules(rules || []);
      setMessageSettings(settings || { maxDimensionLimit: 2000 });
    }).catch(err => {
      console.warn('[ConfiguratorForm] Impossible de charger les règles de messages:', err);
    });
  }, []);

  // 🆕 Vérifier si un produit existe pour les dimensions actuelles
  // ⚠️ MAPPING AXES: Les règles displayExpression utilisent la convention backend:
  //    Backend: X=profondeur, Y=largeur, Z=hauteur
  //    Renderer: sizeX=largeur, sizeZ=profondeur, frontHeight=hauteur
  // WALLMOUNT vient désormais de `modelFormData.wallmount` (état indépendant),
  // plus de la dérivation `type === 'sol-mur' || type === 'mur-haut'` qui
  // confondait type d'installation et mode de fixation.
  // DEC reste alias strict de DECi (dérivé du type d'installation) ; DECc
  // est le `Products.dec` du produit éventuellement déjà sélectionné. Ici on
  // ne dispose pas encore d'un produit (on est en train de chercher s'il y
  // en a un qui matche) ; on évalue donc avec productDec=null → DECc=null.
  // Les règles d'affichage qui veulent un fallback doivent utiliser
  // `DECc ?? DECi`.
  const displayOptions = useMemo(() => {
    const type = modelFormData.type || '';
    const { DEC, DECi, DECc } = resolveDecValues(type, productDec);
    return {
      BOTTOM: !!modelFormData.bottom,
      WALLMOUNT: !!modelFormData.wallmount,
      REAR: !!modelFormData.rear,
      TYPE: type,
      INSTALL: type,
      DEC,
      DECi,
      DECc,
    };
  }, [modelFormData.bottom, modelFormData.wallmount, modelFormData.rear, modelFormData.type, productDec]);

  const hasMatchingProduct = useMemo(() => {
    if (availableProducts.length === 0) return null;
    const largeur = modelFormData.sizeX;
    const hauteur = modelFormData.frontHeight;
    const profondeur = modelFormData.sizeZ;
    return selectProductLocally(availableProducts, profondeur, largeur, hauteur, displayOptions) !== null;
  }, [availableProducts, modelFormData.sizeX, modelFormData.frontHeight, modelFormData.sizeZ, displayOptions]);

  useEffect(() => {
    onHasMatchingProductChange?.(hasMatchingProduct);
  }, [hasMatchingProduct, onHasMatchingProductChange]);

  useEffect(() => {
    handleQuotationNeed();
  }, [modelFormData, hasMatchingProduct, messageRules, selectedTab]);

  // productDec + dimFormulas : recalculer le cache une fois la config 3D et
  // les formules maîtresses disponibles (évite un /price avec mauvaises cotes).
  useEffect(() => {
    if (!isProductConfigReady) return;
    if (dimFormulas == null) return;
    if (modelFormData?.aModifierDimensionsCache) {
      onCacheDimensionsReady?.();
      return;
    }

    const hasUserDims =
      modelFormData?.sizeL > 0 &&
      modelFormData?.sizeH > 0 &&
      (modelFormData?.sizeP1 > 0 || modelFormData?.sizeP2 > 0);
    if (!hasUserDims) {
      onCacheDimensionsReady?.();
      return;
    }

    const recalcKey = [
      selectedProductId,
      productDec,
      dimFormulas ? "formulas" : "fallback",
    ].join("|");
    if (cacheRecalcKeyRef.current === recalcKey) {
      onCacheDimensionsReady?.();
      return;
    }
    cacheRecalcKeyRef.current = recalcKey;

    recalculateDimensions({});
    // cacheDimensionsReady est positionné dans onModelFormBatchChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDec, dimFormulas, isProductConfigReady, selectedProductId]);

  let maxStep;
  if (isXs) {
    maxStep = 2;
  } else if (isSm || isMd) {
    maxStep = 3;
  } else {
    maxStep = 3;
  }

  const setTabChange = (newValue) => {
    setSelectedTab(newValue);
  };

  const resetErrors = () => {
    setDimensionsErrorString(null);
    setDimensionsNeedDevis(false);
    setIsDimensionsError(false);
  };

  const handlePatternChange = (event) => {
    const patternId = event.target.value;
    const found = patterns.find((p) => String(p.id) === String(patternId));
    onModelFormChange("pattern", patternId);
    onModelFormChange("patternName", found?.name ?? "");
  };

  const handleMaterialChange = (material) => {
    onMaterialFormChange("material", material);
  };

  // Default WALLMOUNT par type (imposé sauf `sol-mur` où le toggle est libre) :
  //   - mur-haut, mur, sol-mur (1ère visite) → fixation murale (true)
  //   - sol → fixation au sol (false)
  // Pour `sol-mur`, le dernier choix utilisateur est mémorisé (solMurWallmountRef).
  const defaultWallmountForType = (type) => type !== "sol";

  const handleTypeChange = (type) => {
    const prevType = modelFormData.type;
    if (prevType === "sol-mur") {
      solMurWallmountRef.current = !!modelFormData.wallmount;
    }

    onModelFormChange("type", type);

    const nextWallmount =
      type === "sol-mur"
        ? (solMurWallmountRef.current ?? defaultWallmountForType("sol-mur"))
        : defaultWallmountForType(type);
    onModelFormChange("wallmount", nextWallmount);
    // BOTTOM (face du dessous) — désactivé par défaut pour TOUS les types,
    // y compris 'mur-haut' (Sur façade en hauteur). L'utilisateur l'active
    // explicitement via la checkbox "J'ajoute la face du dessous" (affichée
    // uniquement sur 'mur-haut' par TypeForm). Pas d'auto-activation : c'est
    // une action volontaire du client.
    onModelFormChange("bottom", false);
    onModelFormChange("rear", false);

    handleLChange(modelFormData.sizeL);
    handleP1Change(modelFormData.sizeP1);
    handleP2Change(modelFormData.sizeP2);
    handleHChange(modelFormData.sizeH, type, false);
  };

  const handleWallmountChange = (next) => {
    const value = !!next;
    onModelFormChange("wallmount", value);
    if (modelFormData.type === "sol-mur") {
      solMurWallmountRef.current = value;
    }
  };

  // Checkbox "J'ajoute la face du dessous", affichée UNIQUEMENT pour
  // `mur-haut` (Sur façade en hauteur). Le défaut est false pour tous les
  // types (cf. handleTypeChange ci-dessus) ; le client coche explicitement
  // pour ajouter la face du dessous.
  //
  // On bypass volontairement handleHChange (et son guard previousX) pour
  // éviter toute interaction avec les valeurs précédentes mémorisées : un
  // clic sur la checkbox doit TOUJOURS propager la nouvelle valeur de
  // bottom + déclencher un recalcul des dimensions (la formule maîtresse
  // sizeZ utilise BOTTOM) et la ré-évaluation des règles d'affichage.
  const handleBottomFaceChange = (next) => {
    const nextBottom = !!next;
    onModelFormChange("bottom", nextBottom);
    // Synchroniser previousBottom pour que le prochain handleHChange
    // n'early-return pas faussement à cause d'un état périmé.
    setPreviousBottom(nextBottom);
    recalculateDimensions({ bottom: nextBottom });
  };

  const handleRearFaceChange = (next) => {
    const nextRear = !!next;
    onModelFormChange("rear", nextRear);
    recalculateDimensions({ rear: nextRear });
  };

  // Convention BACKOFFICE des dimFormulas (alignée sur le BO + CacheDimensionsForm) :
  //   sizeX      = profondeur (mm)
  //   sizeY      = largeur (mm)
  //   sizeZ      = hauteur AVANT du cache (mm)
  //   rearHeight = hauteur ARRIERE du cache (mm)
  // Ordre de calcul : sizeX → sizeZ → rearHeight → sizeY.
  //
  // Convention RENDERER côté modelFormData (Three.js) :
  //   modelFormData.sizeX        = largeur                ← BO sizeY
  //   modelFormData.sizeY        = hauteur ARRIERE        ← BO rearHeight
  //   modelFormData.sizeZ        = profondeur             ← BO sizeX
  //   modelFormData.frontHeight  = hauteur AVANT          ← BO sizeZ
  //
  // En l'absence de formules maîtresses chargées depuis
  // GlobalSettings.cache_dim_formulas, on retombe sur le fallback hardcodé
  // historique (champ par champ) — ce qui correspond aussi au comportement du
  // BO quand le champ correspondant est vide.
  // `recalculateDimensions` recalcule TOUS les axes du cache à partir des
  // dimensions d'installation (L/H/P1/P2 + flags WALLMOUNT/BOTTOM/INSTALL +
  // DEC/DECi/DECc) et des formules maîtresses.
  //
  // Trois axes peuvent être "forcés" individuellement par un input utilisateur
  // dans CacheDimensionsForm (étape 4, onglet Cache) — passer un flag à `true`
  // pour conserver la valeur saisie sur cet axe au lieu de la recalculer :
  //   - forceFlags.width = true → conserve modelFormData.sizeX (largeur).
  //   - forceFlags.depth = true → conserve modelFormData.sizeZ (profondeur).
  //   - forceFlags.rearY = true → conserve modelFormData.sizeY (hauteur
  //                                arrière) et recalcule frontZ via la pente.
  //
  // Verrou implicite : si `modelFormData.aModifierDimensionsCache` est true
  // (l'utilisateur a modifié au moins une dimension dans l'onglet Cache), les
  // trois axes sont automatiquement forcés depuis modelFormData — un recalcul
  // déclenché par un changement d'installation (L/H/P1/P2/type/bottom) ou par
  // l'arrivée asynchrone de productDec ne pourra plus écraser les valeurs
  // saisies. L'utilisateur doit cliquer "Réinitialiser" dans l'onglet Cache
  // pour revenir aux formules maîtresses (le bouton remet le flag à false).
  //
  // Minimums DURS des dimensions du cache : seuils éditables depuis /products
  // (GlobalSettings.cache_dim_minimums), avec fallback sur les valeurs par
  // défaut. La hauteur avant est esclave de l'arrière (cf. recalculateDimensions),
  // donc sans minimum propre.
  const cacheMinimums = cacheMinimumsConfig ?? CACHE_DIM_HARD_MINIMUMS;

  // Convention BACKOFFICE des dimFormulas (sizeX = profondeur, sizeY = largeur,
  // sizeZ = hauteur avant, rearHeight = hauteur arrière) — cf. les en-têtes
  // de la fonction historique en haut du fichier.
  const recalculateDimensions = (updatedValues = {}) => {
    const sin2Degrees = Math.sin((2 * Math.PI) / 180);

    const newFormData = { ...modelFormData, ...updatedValues };

    // Verrous PAR AXE : chaque axe du cache saisi manuellement (largeur /
    // profondeur / hauteur arrière) est figé tant que l'utilisateur ne
    // réinitialise pas OU ne modifie pas la dimension d'installation
    // correspondante (les handlers L/H/P1/P2 relâchent l'axe concerné).
    const forceFromWidth = !!newFormData.cacheManualWidth;
    const forceFromDepth = !!newFormData.cacheManualDepth;
    const forceFromRearY = !!newFormData.cacheManualRearY;
    const isCacheManuallyEdited =
      forceFromWidth || forceFromDepth || forceFromRearY;

    const sizeL = newFormData.sizeL;
    const sizeH = newFormData.sizeH;
    const P1 = newFormData.sizeP1;
    const P2 = newFormData.sizeP2;

    const { DEC, DECi, DECc } = resolveDecValues(newFormData.type, productDec);
    const formulaVars = {
      L: sizeL,
      H: sizeH,
      P1,
      P2,
      DEC,
      DECi,
      DECc,
      BOTTOM: !!newFormData.bottom,
      WALLMOUNT: !!newFormData.wallmount,
      HTOP: false,
      REAR: !!newFormData.rear,
      INSTALL_TYPE: newFormData.type,
      INSTALL: newFormData.type,
    };

    // 1) Profondeur (renderer sizeZ, BO sizeX).
    let depthX;
    if (forceFromDepth) {
      depthX = newFormData.sizeZ;
    } else {
      depthX = evalCacheDimFormula(dimFormulas?.sizeX, formulaVars);
      if (depthX === null) {
        if (P1 + 80 >= P2 + 30) {
          depthX = P1 + 80;
        } else {
          depthX = P2 + 30;
        }
        if (depthX < 250) depthX = 250;
      }
    }

    // 2) Hauteur avant (renderer frontHeight, BO sizeZ).
    //
    // FALLBACK STRICTEMENT ALIGNÉ SUR LE BO (products/page.jsx#recalcCacheDimensions
    // et threeFiber/preview.jsx) :
    //   wallmount + bottom → +70
    //   wallmount seul     → +40
    //   sinon (sol)        → +30
    const isWallmount = !!newFormData.wallmount;
    const hasBottom = !!newFormData.bottom;
    let frontZ = evalCacheDimFormula(dimFormulas?.sizeZ, formulaVars, { sizeX: depthX });
    if (frontZ === null) {
      let hFactor = 30;
      if (isWallmount && hasBottom) hFactor = 70;
      else if (isWallmount) hFactor = 40;
      frontZ = Math.max(sizeH + hFactor, 250);
    }

    // 3) Hauteur arrière (renderer sizeY) + hauteur AVANT esclave.
    //    Convention physique : arrière = avant + pente, où pente = sin(2°)·(profondeur − 15).
    //    - L'utilisateur pilote la hauteur ARRIÈRE ; l'AVANT est recalculé via la
    //      pente dès qu'on modifie l'arrière OU la profondeur.
    //    - En mode auto (non forcé), l'arrière découle de l'avant calculé.
    //    SEUIL MINIMUM = sur la hauteur AVANT (cacheMinimums.minFrontHeight) : si
    //    l'avant résultant passe sous ce seuil, on remonte la hauteur arrière à la
    //    valeur qui ramène l'avant exactement au minimum (avant = minFront).
    const minFront = cacheMinimums?.minFrontHeight ?? 0;
    let rearY;
    if (forceFromRearY || forceFromDepth) {
      rearY = newFormData.sizeY;
    } else {
      rearY = Math.ceil(frontZ + sin2Degrees * (depthX - 15));
    }
    const heightAdjusted = applyMinFrontToRearHeight(
      rearY,
      depthX,
      minFront
    );
    rearY = heightAdjusted.rearY;
    frontZ = heightAdjusted.frontZ;

    // 4) Largeur (renderer sizeX, BO sizeY). On passe rearHeight comme variable
    //    intermédiaire à la formule maîtresse sizeY (compat BO).
    const rearHeightForFormula =
      evalCacheDimFormula(
        dimFormulas?.rearHeight,
        formulaVars,
        { sizeX: depthX, sizeZ: frontZ }
      ) ?? rearY;

    let widthY;
    if (forceFromWidth) {
      widthY = newFormData.sizeX;
    } else {
      widthY = evalCacheDimFormula(
        dimFormulas?.sizeY,
        formulaVars,
        { sizeX: depthX, sizeZ: frontZ, rearHeight: rearHeightForFormula }
      );
      if (widthY === null) {
        widthY = sizeL + 90;
        if (sizeL < 310) widthY = 400;
      }
    }

    const cacheUpdates = {
      frontHeight: frontZ,
      sizeH,
      sizeL,
      sizeP1: P1,
      sizeP2: P2,
      noClipX: widthY - sizeL,
      noClipY: frontZ - sizeH,
      noClipZP1: depthX - P1,
      noClipZP2: depthX - P2,
      sizeX: widthY,
      sizeY: rearY,
      sizeZ: depthX,
      cacheManualWidth: forceFromWidth,
      cacheManualDepth: forceFromDepth,
      cacheManualRearY: forceFromRearY,
      aModifierDimensionsCache: isCacheManuallyEdited,
    };

    if (onModelFormBatchChange) {
      onModelFormBatchChange(cacheUpdates);
    } else {
      onModelFormChange("frontHeight", frontZ);
      onModelFormChange("sizeH", sizeH);
      onModelFormChange("sizeL", sizeL);
      onModelFormChange("sizeP1", P1);
      onModelFormChange("sizeP2", P2);
      onModelFormChange("noClipX", widthY - sizeL);
      onModelFormChange("noClipY", frontZ - sizeH);
      onModelFormChange("noClipZP1", depthX - P1);
      onModelFormChange("noClipZP2", depthX - P2);
      onModelFormChange("sizeX", widthY);
      onModelFormChange("sizeY", rearY);
      onModelFormChange("sizeZ", depthX);
      onModelFormChange("cacheManualWidth", forceFromWidth);
      onModelFormChange("cacheManualDepth", forceFromDepth);
      onModelFormChange("cacheManualRearY", forceFromRearY);
      onModelFormChange("aModifierDimensionsCache", isCacheManuallyEdited);
    }

    return {
      ...newFormData,
      ...cacheUpdates,
      sizeL,
      sizeH,
      sizeP1: P1,
      sizeP2: P2,
    };
  };

  useImperativeHandle(ref, () => ({
    prepareForCart() {
      const flushed = dimensionsFormRef.current?.flushPendingInstallDimensions?.() ?? {};
      if (Object.keys(flushed).length === 0) {
        return { ...modelFormData };
      }
      return recalculateDimensions(flushed);
    },
  }), [modelFormData]);

  // Modifier une dimension d'installation relâche le verrou manuel de l'axe de
  // cache correspondant (la dimension de cache se recalcule depuis les formules
  // maîtresses), tout en conservant les éventuels verrous sur les autres axes.
  //   L  → largeur (cacheManualWidth)
  //   H  → hauteur arrière/avant (cacheManualRearY)
  //   P1/P2 → profondeur (cacheManualDepth)
  const handleLChange = (L) => {
    if (L == previousL) return;
    setPreviousL(L);
    recalculateDimensions({ sizeL: L, cacheManualWidth: false });
  };

  const handleHChange = (H, type, bottom) => {
    if (H == previousH && previousType == type && previousBottom == bottom)
      return;
    setPreviousH(H);
    setPreviousType(type);
    setPreviousBottom(bottom);
    recalculateDimensions({
      sizeH: H,
      type: type || modelFormData.type,
      bottom: bottom !== undefined ? bottom : modelFormData.bottom,
      cacheManualRearY: false,
    });
  };

  const handleP1Change = (P1) => {
    if (P1 == previousP1) return;
    setPreviousP1(P1);
    recalculateDimensions({ sizeP1: P1, cacheManualDepth: false });
  };

  const handleP2Change = (P2) => {
    if (P2 == previousP2) return;
    setPreviousP2(P2);
    recalculateDimensions({ sizeP2: P2, cacheManualDepth: false });
  };

  // Onglet "Cache" de l'étape 4 : l'utilisateur saisit la profondeur, la
  // largeur ou la hauteur arrière du cache. Chaque handler force l'axe
  // correspondant pour que la valeur saisie soit préservée et que les autres
  // axes restent cohérents (formules maîtresses pour les non-forcés, pente
  // 2° pour la relation front/rear).
  const handleZChange = (Z) => {
    recalculateDimensions({ sizeZ: Z, cacheManualDepth: true });
  };

  const handleXChange = (X) => {
    recalculateDimensions({ sizeX: X, cacheManualWidth: true });
  };

  const handleRearYChange = (Y) => {
    recalculateDimensions({ sizeY: Y, cacheManualRearY: true });
  };

  const handleResetCacheDimensions = () => {
    recalculateDimensions({
      cacheManualWidth: false,
      cacheManualDepth: false,
      cacheManualRearY: false,
    });
  };

  // Lien de partage (nouveau format) : seules les cotes d'installation sont
  // dans l'URL → on recalcule le cache depuis les formules maîtresses.
  // Les anciens liens « pétés » (aModifierDimensionsCache dans l'URL) sont
  // ignorés côté parseShareParamsFromSearch ; on recalcule aussi ici.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!urlHasInstallDimensions(window.location.search)) return;
    if (urlHasCustomCacheDimensions(window.location.search)) return;
    if (modelFormData?.aModifierDimensionsCache) return;

    const hasUserDims =
      modelFormData?.sizeL > 0 &&
      modelFormData?.sizeH > 0 &&
      (modelFormData?.sizeP1 > 0 || modelFormData?.sizeP2 > 0);
    if (!hasUserDims) return;

    const recalcKey = [
      modelFormData.sizeL,
      modelFormData.sizeH,
      modelFormData.sizeP1,
      modelFormData.sizeP2,
      modelFormData.type,
      modelFormData.wallmount,
      modelFormData.bottom,
      productDec,
      dimFormulas ? "formulas" : "fallback",
    ].join("|");
    if (urlInstallRecalcKeyRef.current === recalcKey) return;
    urlInstallRecalcKeyRef.current = recalcKey;

    recalculateDimensions({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    modelFormData.sizeL,
    modelFormData.sizeH,
    modelFormData.sizeP1,
    modelFormData.sizeP2,
    modelFormData.type,
    modelFormData.wallmount,
    modelFormData.bottom,
    productDec,
    dimFormulas,
  ]);

  const handleQuotationNeed = (
    LU = modelFormData.sizeL,
    HU = modelFormData.sizeH,
    P1U = modelFormData.sizeP1,
    P2U = modelFormData.sizeP2,
    H = modelFormData.frontHeight,
    L = modelFormData.sizeX,
    P = modelFormData.sizeZ,
    type = modelFormData.type,
    bottom = modelFormData.bottom,
    noClipX = modelFormData.noClipX,
    noClipY = modelFormData.noClipY,
    noClipZP1 = modelFormData.noClipZP1,
    noClipZP2 = modelFormData.noClipZP2
  ) => {
    resetErrors();
    
    // 🆕 Utiliser les règles dynamiques si disponibles
    if (messageRules && messageRules.length > 0) {
      // Construire le contexte d'évaluation. On expose à la fois la convention
      // historique (minuscules) et les alias majuscules unifiés avec le reste
      // du système (INSTALL, WALLMOUNT, BOTTOM, DEC/DECi/DECc).
      const { DEC, DECi, DECc } = resolveDecValues(type, productDec);
      const WALLMOUNT = !!modelFormData.wallmount;
      const context = {
        L, H, P,
        LU, HU, P1U, P2U,
        type,
        bottom,
        INSTALL: type,
        BOTTOM: !!bottom,
        WALLMOUNT,
        DEC,
        DECi,
        DECc,
        selectedTab,
        noClipX, noClipY, noClipZP1, noClipZP2,
        hasMatchingProduct,
        MAX_DIMENSION: messageSettings.maxDimensionLimit || 2000,
      };
      
      // Évaluer les règles et trouver la première qui correspond
      const matchingRule = evaluateMessageRules(messageRules, context);
      
      if (matchingRule) {
        const needQuotation = matchingRule.needsQuotation || false;
        const noPrice = matchingRule.blocksPrice || needQuotation;
        const error = matchingRule.isError || false;
        
        onQuotationChange("isNeeded", needQuotation);
        onQuotationChange("noPrice", noPrice);
        setDimensionsErrorString(matchingRule.message);
        setIsDimensionsError(error);
        setDimensionsNeedDevis(needQuotation);
        return;
      }
      
      // Aucune règle ne correspond → pas d'erreur
      onQuotationChange("isNeeded", false);
      onQuotationChange("noPrice", false);
      setDimensionsErrorString(null);
      setIsDimensionsError(false);
      setDimensionsNeedDevis(false);
      return;
    }
    
    // ⚠️ FALLBACK: Logique hardcodée si les règles ne sont pas chargées
    // Cette partie sera exécutée uniquement si l'API ne répond pas
    let errorString = null;
    let error = false;
    let needQuotation = false;
    let noPrice = false;

    // Vérification dynamique basée sur les produits disponibles
    if (hasMatchingProduct === false) {
      errorString =
        "Aucun produit ne correspond à ces dimensions. Un devis personnalisé est nécessaire.";
      needQuotation = true;
    } else if (hasMatchingProduct === null) {
      // Produits pas encore chargés → fallback sur les anciennes limites hardcodées
      if (L > 1350) {
        errorString =
          "La largeur de votre cache dépasse la limite de notre configurateur.";
        needQuotation = true;
      }

      if (H > 1450) {
        errorString =
          "La hauteur de votre cache dépasse la limite de notre configurateur.";
        needQuotation = true;
      }

      if (L > 1350 && H > 1450) {
        errorString =
          "Les dimensions de votre cache dépassent nos limites techniques de fabrication.";
        noPrice = true;
        needQuotation = false;
      }
    }

    let noclipErrors = 0;

    // ⚠️ Note historique : ce fallback utilisait noClipX/Y/ZP1/ZP2 qui valaient
    // en réalité les dimensions d'installation brutes (LU/HU/P1U/P2U), alors que
    // la légende des règles de messages décrit ces variables comme des MARGES.
    // Depuis le fix, recalculateDimensions stocke bien des marges dans noClip*.
    // Pour ne pas changer le comportement de ce fallback (qui ne tourne qu'en
    // l'absence de règles dynamiques), on tape directement sur LU/HU/P1U/P2U.
    if (LU < 310) {
      errorString =
        "Attention : la largeur de votre unité ne permet pas un ajustement parfait du cache.";
      noclipErrors = noclipErrors + 1;
    }

    if (type == "mur-haut" && bottom) {
      if (HU < 430) {
        errorString =
          "Attention : la hauteur de votre unité ne permet pas un ajustement parfait du cache.";
        noclipErrors = noclipErrors + 1;
      }
    } else if (type == "mur-haut" || type == "sol-mur") {
      if (HU < 460) {
        errorString =
          "Attention : la hauteur de votre unité ne permet pas un ajustement parfait du cache.";
        noclipErrors = noclipErrors + 1;
      }
    } else {
      if (HU < 470) {
        errorString =
          "Attention : la hauteur de votre unité ne permet pas un ajustement parfait du cache.";
        noclipErrors = noclipErrors + 1;
      }
    }

    if (P1U + 80 >= P2U + 30) {
      if (P1U < 250 - 80) {
        errorString =
          "Attention : la profondeur de votre unité ne permet pas un ajustement parfait du cache.";
        noclipErrors = noclipErrors + 1;
      }
    } else {
      if (P2U < 250 - 30) {
        errorString =
          "Attention : la profondeur de votre unité ne permet pas un ajustement parfait du cache.";
        noclipErrors = noclipErrors + 1;
      }
    }

    if (noclipErrors > 1) {
      errorString =
        "Les dimensions renseignées ne permettent pas un ajustement parfait.";
    }

    if (selectedTab === 1) {
      if (L < LU + 85) {
        errorString =
          "Attention : la largeur choisie ne permettra pas la bonne installation de votre cache et risque d'aboutir au contact entre l'unité et le cache, qu'il faut absolument éviter.";
        error = true;
        needQuotation = false;
      }

      if (type == "mur-haut" && bottom) {
        if (H < HU + 60) {
          errorString =
            "Attention : la hauteur choisie permettra l'installation de votre cache mais sous-entend une prise de mesure très précise et une pose particulièrement soignée de votre part.";
          needQuotation = false;
        }

        if (H < HU + 45) {
          errorString =
            "Attention : la hauteur choisie ne permettra pas la bonne installation de votre cache dans les conditions normalement prévues.";
          error = true;
          needQuotation = false;
        }
      } else if (type == "mur-haut" || type == "sol-mur") {
        if (H < HU + 20) {
          errorString =
            "Attention : la hauteur choisie permettra l'installation de votre cache mais sous-entend une prise de mesure très précise et une pose particulièrement soignée de votre part.";
          needQuotation = false;
        }

        if (H < HU + 5) {
          errorString =
            "Attention : la hauteur choisie ne permettra pas la bonne installation de votre cache dans les conditions normalement prévues.";
          error = true;
          needQuotation = false;
        }
      } else {
        if (H < HU + 10) {
          errorString =
            "Attention : la hauteur choisie permettra l'installation de votre cache mais sous-entend une prise de mesure très précise et une pose particulièrement soignée de votre part.";
          needQuotation = false;
        }

        if (H < HU + 5) {
          errorString =
            "Attention : la hauteur choisie ne permettra pas la bonne installation de votre cache dans les conditions normalement prévues.";
          error = true;
          needQuotation = false;
        }
      }

      if (P < P1U + 80) {
        errorString =
          "Attention : la profondeur choisie risque de pénaliser le flux d'air.";
        needQuotation = false;
      }

      if (P < P1U + 50) {
        errorString =
          "Attention : la profondeur choisie ne permettra pas la bonne installation de votre cache et risque d'aboutir au contact entre l'unité et le cache, qu'il faut absolument éviter.";
        error = true;
        needQuotation = false;
      }

      if (P < P2U + 20) {
        errorString =
          "Attention : la profondeur choisie ne permettra pas la bonne installation de votre cache et risque d'aboutir au contact entre les supports et le cache, qu'il faut absolument éviter.";
        error = true;
        needQuotation = false;
      }
    }

    // Limites techniques absolues (garde-fous de sécurité)
    const MAX_DIMENSION = 2000;
    
    if (L > MAX_DIMENSION || H > MAX_DIMENSION || P > MAX_DIMENSION) {
      if (L > MAX_DIMENSION && H > MAX_DIMENSION) {
        errorString =
          "Les dimensions de votre cache dépassent nos limites techniques de fabrication.";
      } else if (L > MAX_DIMENSION) {
        errorString =
          "La largeur de votre cache dépasse nos limites techniques de fabrication.";
      } else if (H > MAX_DIMENSION) {
        errorString =
          "La hauteur de votre cache dépasse nos limites techniques de fabrication.";
      } else if (P > MAX_DIMENSION) {
        errorString =
          "La profondeur de votre cache dépasse nos limites techniques de fabrication.";
      }
      noPrice = true;
      needQuotation = false;
    }

    if (needQuotation) noPrice = needQuotation;
    onQuotationChange("isNeeded", needQuotation);
    onQuotationChange("noPrice", noPrice);
    setDimensionsErrorString(errorString);
    setIsDimensionsError(error);
    setDimensionsNeedDevis(needQuotation);
  };

  const renderSteps = () => {
    if (isXs) {
      if (step === 0) {
        return (
          <>
            <InstallationTypeNotice isMobile variant="inline" />
            <PatternForm
              selectedPattern={modelFormData.pattern}
              onPatternChange={handlePatternChange}
              patterns={patterns}
            />
            <TextureForm
              selectedMaterial={materialFormData}
              onMaterialChange={handleMaterialChange}
              textures={textures}
            />
          </>
        );
      } else if (step === 1) {
        return (
          <TypeForm
            selectedType={modelFormData.type}
            onTypeChange={handleTypeChange}
            wallmount={!!modelFormData.wallmount}
            onWallmountChange={handleWallmountChange}
            bottom={!!modelFormData.bottom}
            onBottomChange={handleBottomFaceChange}
            rear={!!modelFormData.rear}
            onRearChange={handleRearFaceChange}
          />
        );
      } else if (step === 2) {
        return (
          <DimensionsForm
            ref={dimensionsFormRef}
            installationType={modelFormData.type}
            sizeL={modelFormData.sizeL}
            sizeH={modelFormData.sizeH}
            sizeP1={modelFormData.sizeP1}
            sizeP2={modelFormData.sizeP2}
            sizeX={modelFormData.sizeX}
            sizeY={modelFormData.sizeY}
            sizeZ={modelFormData.sizeZ}
            frontHeight={modelFormData.frontHeight}
            onSizeLChange={handleLChange}
            onSizeHChange={handleHChange}
            onSizeP1Change={handleP1Change}
            onSizeP2Change={handleP2Change}
            onSizeXChange={handleXChange}
            onRearYChange={handleRearYChange}
            onSizeZChange={handleZChange}
            dimensionsErrorString={dimensionsErrorString}
            dimensionsNeedDevis={dimensionsNeedDevis}
            isDimensionsError={isDimensionsError}
            setTabChange={setTabChange}
            bottom={modelFormData.bottom}
            rear={!!modelFormData.rear}
            wallmount={!!modelFormData.wallmount}
            onMetadataInfosChange={onMetadataInfosChange}
            onResetCacheDimensions={handleResetCacheDimensions}
            aModifierDimensionsCache={!!modelFormData.aModifierDimensionsCache}
            dimFormulas={dimFormulas}
            cacheMinimums={cacheMinimums}
            productDec={productDec}
            onInstallDimDraftChange={onInstallDimDraftChange}
          />
        );
      }
    } else {
      if (step === 0) {
        return (
          <PatternForm
            selectedPattern={modelFormData.pattern}
            onPatternChange={handlePatternChange}
            patterns={patterns}
          />
        );
      } else if (step === 1) {
        return (
          <TextureForm
            selectedMaterial={materialFormData}
            onMaterialChange={handleMaterialChange}
            textures={textures}
          />
        );
      } else if (step === 2) {
        return (
          <TypeForm
            selectedType={modelFormData.type}
            onTypeChange={handleTypeChange}
            wallmount={!!modelFormData.wallmount}
            onWallmountChange={handleWallmountChange}
            bottom={!!modelFormData.bottom}
            onBottomChange={handleBottomFaceChange}
            rear={!!modelFormData.rear}
            onRearChange={handleRearFaceChange}
          />
        );
      } else if (step === 3) {
        return (
          <DimensionsForm
            ref={dimensionsFormRef}
            installationType={modelFormData.type}
            sizeL={modelFormData.sizeL}
            sizeH={modelFormData.sizeH}
            sizeP1={modelFormData.sizeP1}
            sizeP2={modelFormData.sizeP2}
            sizeX={modelFormData.sizeX}
            sizeY={modelFormData.sizeY}
            sizeZ={modelFormData.sizeZ}
            frontHeight={modelFormData.frontHeight}
            onSizeLChange={handleLChange}
            onSizeHChange={handleHChange}
            onSizeP1Change={handleP1Change}
            onSizeP2Change={handleP2Change}
            onSizeXChange={handleXChange}
            onRearYChange={handleRearYChange}
            onSizeZChange={handleZChange}
            dimensionsErrorString={dimensionsErrorString}
            dimensionsNeedDevis={dimensionsNeedDevis}
            isDimensionsError={isDimensionsError}
            setTabChange={setTabChange}
            bottom={modelFormData.bottom}
            rear={!!modelFormData.rear}
            wallmount={!!modelFormData.wallmount}
            onMetadataInfosChange={onMetadataInfosChange}
            onResetCacheDimensions={handleResetCacheDimensions}
            aModifierDimensionsCache={!!modelFormData.aModifierDimensionsCache}
            dimFormulas={dimFormulas}
            cacheMinimums={cacheMinimums}
            productDec={productDec}
            onInstallDimDraftChange={onInstallDimDraftChange}
          />
        );
      }
    }
  };

  return (
    <div
      style={{
        justifyItems: isXs ? undefined : "center",
      }}
    >
      {renderSteps()}

      <div
        style={{
          mt: 4,
          display: "flex",
          marginTop: isXs ? "25px" : "25px",
          justifyContent: "space-evenly",
          paddingTop: isXs ? (step === 1 ? "10px" : "80px") : "25px",
          paddingBottom: isXs ? "10px" : "30px",
          width: isXs ? undefined : "80%",
          justifyContent: isXs ? "space-evenly" : "space-between",
          alignItems: isXs ? undefined : "center",
        }}
      >
        {isXs == false && isSm == false && step == 0 && (
          <div style={{ minWidth: "40%" }}></div>
        )}
        {step > 0 && (
          <FormButtonPrevious
            onPrevStep={handleStepChange}
            currentStep={step}
          />
        )}
        {step < maxStep && (
          <FormButtonNext onNextStep={handleStepChange} currentStep={step} />
        )}
        {isXs == false && isSm == false && step >= maxStep && (
          <AddToCartButton
            price={price}
            quotationDatas={quotationDatas}
            isDisabled={isButtonCartDisabled}
            modelFormData={modelFormData}
            materialFormData={materialFormData}
            handleAddToCart={handleAddToCart}
            hidePrice={hidePrice}
            isPriceStale={isPriceStale}
            overrideStyle={true}
          />
        )}
      </div>
    </div>
  );
});

ConfiguratorForm.displayName = "ConfiguratorForm";

export default ConfiguratorForm;
